import React, { forwardRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { useTranslation } from "react-i18next";
import { DashlyFilterClearField } from "../DashlyFilterClearField/DashlyFilterClearField";
import { DashlyDropdown, type DashlyDropdownProps } from "../DashlyDropdown/DashlyDropdown";

export type DashlyFilterDropdownProps = Omit<DashlyDropdownProps, "controlVariant" | "showClear"> & {
    showClear?: boolean;
    clearValue?: unknown;
    onClear?: () => void;
};

/**
 * Filter-bar dropdown — Dashly styling with filter tokens. Clear button is enabled by default
 * via {@link DashlyFilterClearField} (filter variant hides Prime's built-in clear icon).
 */
const DashlyFilterDropdown = forwardRef<Dropdown, DashlyFilterDropdownProps>(function DashlyFilterDropdown(
    {
        className,
        panelClassName,
        showClear = true,
        clearValue = null,
        onClear,
        value,
        onChange,
        controlSize = "md",
        ...rest
    },
    ref,
) {
    const { t } = useTranslation();
    const hasValue = value !== undefined && value !== null && value !== clearValue && value !== "";
    const clearLabel = t("common.action.clear_filter");

    const handleClear = () => {
        onChange?.({ value: clearValue } as Parameters<NonNullable<typeof onChange>>[0]);
        onClear?.();
    };

    const dropdown = (
        <DashlyDropdown
            ref={ref}
            {...rest}
            value={value}
            onChange={onChange}
            controlSize={controlSize}
            controlVariant="filter"
            showClear={false}
            className={classNames(className)}
            panelClassName={classNames("spoofy-filter-dropdown-panel", panelClassName)}
        />
    );

    if (!showClear) {
        return dropdown;
    }

    return (
        <DashlyFilterClearField
            hasValue={hasValue}
            onClear={handleClear}
            clearLabel={clearLabel}
            controlType="select"
            controlSize={controlSize}
            className="w-full"
        >
            {dropdown}
        </DashlyFilterClearField>
    );
});

DashlyFilterDropdown.displayName = "DashlyFilterDropdown";

export { DashlyFilterDropdown };
